import React, { FC, ReactNode } from 'react';
import { Button } from 'reactstrap';
import { ProductAddonModel, ProductModel, SurchargeModel } from '../../shared/orders/product';
import { RaffleWithUserDetails } from '../../shared/orders/raffle';
import { Currency } from '../components/Currency';
import { isRaffleClosed } from '../models';
import { useTranslation } from '../translations';
import { classNames } from '.';

interface ProductPriceProps {
  price: ReactNode;
  was?: ReactNode;
}

interface ProductRawPrice {
  price: number;
  was?: number;
}

export function getAddonPrice(
  addon: Pick<ProductAddonModel, 'discounts' | 'price'>,
  quantity = 1,
): ProductPriceProps {
  const product = {
    ...addon,
    surcharges: [],
  };

  return getProductPrice(product, quantity);
}

export function getProductPrice(
  product: Pick<ProductModel, 'discounts' | 'price' | 'surcharges'>,
  quantity = 1,
): ProductPriceProps {
  const { price, was } = getProductRawPrice(product, quantity);

  return {
    price: <Currency value={price} />,
    was: was === undefined ? undefined : <Currency value={was} />,
  };
}

function getProductRawPrice(
  {
    discounts,
    price: rawPrice,
    surcharges,
  }: Pick<ProductModel, 'discounts' | 'price' | 'surcharges'>,
  quantity = 1,
): ProductRawPrice {
  /*
   * This code was added in order to comply with the FTC Ruling below.
   * https://www.ftc.gov/legal-library/browse/rules/rulemaking-unfair-or-deceptive-fees
   *
   * In the future, we may want to store the convention country as tax based charges are
   * not necessary to display in the United States as it varies wildly state by state.
   * However it IS required in the UK and most EU countries due to VAT being pretty standard.
   */

  let price = rawPrice;

  let tax: SurchargeModel | undefined;

  for (const charge of surcharges) {
    /*
     * we need to delay tax charges to the very end as those go ontop of surcharges
     * we also know that products only have ONE tax charge
     */
    if (charge.type === 'tax') {
      tax = charge;
      continue;
    }

    if (charge.amountType === 'fixed') {
      price += charge.amount;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (charge.amountType === 'percent') {
      // this sucks why is this how the amounts are stored?
      price *= 1 + 0.0001 * charge.amount;
    }
  }

  if (tax) {
    price *= 1 + 0.0001 * tax.amount;
  }

  if (discounts.length === 0) {
    return { price: price * quantity };
  }

  return {
    price: Math.min(...discounts.map(({ price: discountPrice }) => discountPrice)) * quantity,
    was: price * quantity,
  };
}

export const ProductPrice: FC<{
  readonly product: ProductModel;
  readonly showDiscount?: boolean;
}> = ({ product, showDiscount }) => {
  const { was, price } = getProductPrice(product);
  const { ts } = useTranslation();
  return (
    <div className={classNames({ productWithDiscount: !!was }, 'productPurchaseBarPrice')}>
      {price}
      {showDiscount && was && (
        <>
          <br />
          <small>
            {ts('products.was')} {was}
          </small>
        </>
      )}
    </div>
  );
};

interface BuyButtonProps {
  readonly product: ProductModel;
  readonly label?: string;
  readonly raffle?: RaffleWithUserDetails;
  readonly onSelect: (id: number) => void;
}

export const BuyButton: FC<BuyButtonProps> = ({ product, onSelect, raffle: userRaffle, label }) => {
  const { ts } = useTranslation();
  const { id, stockStatus, raffle } = product;
  label ??= ts('common.add_to_cart');

  if (isRaffleClosed(userRaffle, raffle)) {
    return (
      <Button color="light" disabled id={`closedRaffle${id}`} outline>
        {ts('products.closed Raffle')}
      </Button>
    );
  }

  if (stockStatus === 'soldOut' && raffle?.overflowEntries !== true) {
    return (
      <Button disabled outline>
        {ts('common.sold_out')}
      </Button>
    );
  }

  return (
    <Button
      className="buy"
      color="success"
      id={`selectProduct${id}`}
      onClick={() => {
        onSelect(id);
      }}
    >
      {label}
    </Button>
  );
};
